import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useCreateClient, useUpdateClient, useClient } from "@/features/clients/hooks/useClients";
import { PageLoader } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import type { Address } from "@/features/clients/types";
import { usePageTitle } from "@/hooks/usePageTitle";

const emptyAddress = (): Address => ({
  street: "", building_number: "", apartment_number: "", city: "", postal_code: "", country: "Polska",
});

const emptyForm = () => ({
  name: "",
  short_name: "",
  nip: "",
  regon: "",
  krs: "",
  is_vat_payer: true,
  eu_vat_number: "",
  registered_address: emptyAddress(),
  correspondence_address: null as Address | null,
  bank_account_number: "",
  bank_name: "",
  contact_person: "",
  contact_email: "",
  contact_phone: "",
  website: "",
  notes: "",
  is_active: true,
});

function AddressFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Address;
  onChange: (a: Address) => void;
}) {
  const set = (k: keyof Address, v: string) => onChange({ ...value, [k]: v });
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 flex flex-col gap-3">
      <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Ulica</label>
          <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={value.street}
            onChange={(e) => set("street", e.target.value)}
            placeholder="np. ul. Przykładowa"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nr budynku</label>
          <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={value.building_number}
            onChange={(e) => set("building_number", e.target.value)}
            placeholder="np. 10"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Nr lokalu</label>
          <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={value.apartment_number ?? ""}
            onChange={(e) => set("apartment_number", e.target.value)}
            placeholder="opcjonalnie"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kod pocztowy</label>
          <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={value.postal_code}
            onChange={(e) => set("postal_code", e.target.value)}
            placeholder="00-000"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Miejscowość</label>
          <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={value.city}
            onChange={(e) => set("city", e.target.value)}
            placeholder="np. Warszawa"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kraj</label>
        <input
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          value={value.country ?? "Polska"}
          onChange={(e) => set("country", e.target.value)}
        />
      </div>
    </div>
  );
}

export function ClientFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: existing, isLoading } = useClient(id ?? "");
  usePageTitle(isEdit ? (existing && `Edycja: ${existing.name}`) : null);
  const [form, setForm] = useState(emptyForm());
  const [useCorr, setUseCorr] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name,
        short_name: existing.short_name,
        nip: existing.nip,
        regon: existing.regon,
        krs: existing.krs,
        is_vat_payer: existing.is_vat_payer,
        eu_vat_number: existing.eu_vat_number,
        registered_address: existing.registered_address,
        correspondence_address: existing.correspondence_address,
        bank_account_number: existing.bank_account_number,
        bank_name: existing.bank_name,
        contact_person: existing.contact_person,
        contact_email: existing.contact_email,
        contact_phone: existing.contact_phone,
        website: existing.website,
        notes: existing.notes,
        is_active: existing.is_active,
      });
      setUseCorr(!!existing.correspondence_address);
    }
  }, [existing]);

  const createMutation = useCreateClient();
  const updateMutation = useUpdateClient(id ?? "");

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Nazwa jest wymagana";
    if (!form.nip.trim()) errs.nip = "NIP jest wymagany";
    if (!form.registered_address.street.trim()) errs.street = "Ulica jest wymagana";
    if (!form.registered_address.city.trim()) errs.city = "Miejscowość jest wymagana";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      ...form,
      correspondence_address: useCorr ? form.correspondence_address ?? emptyAddress() : null,
    };
    try {
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        navigate(`/clients/${id}`);
      } else {
        const created = await createMutation.mutateAsync(payload);
        navigate(`/clients/${created.id}`);
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors;
      if (data) {
        const parsed: Record<string, string> = {};
        for (const [k, v] of Object.entries(data)) {
          parsed[k] = Array.isArray(v) ? v[0] : String(v);
        }
        setErrors(parsed);
      }
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (isEdit && isLoading) return <PageLoader />;

  return (
    <div className="max-w-2xl">
      <Link
        to="/clients"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Klienci
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        {isEdit ? "Edytuj klienta" : "Nowy klient"}
      </h1>

      {/* Basic info */}
      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dane firmy</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Nazwa <span className="text-red-500">*</span>
            </label>
            <input
              className={`w-full border rounded px-3 py-2 text-sm ${errors.name ? "border-red-400" : "border-gray-300"}`}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="np. Energa Ciepło S.A."
            />
            {errors.name && <p className="text-xs text-red-600 mt-0.5">{errors.name}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Nazwa skrócona</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.short_name}
              onChange={(e) => setForm({ ...form, short_name: e.target.value })}
              placeholder="opcjonalnie"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              NIP <span className="text-red-500">*</span>
            </label>
            <input
              className={`w-full border rounded px-3 py-2 text-sm font-mono ${errors.nip ? "border-red-400" : "border-gray-300"}`}
              value={form.nip}
              onChange={(e) => setForm({ ...form, nip: e.target.value })}
              placeholder="1234567890"
            />
            {errors.nip && <p className="text-xs text-red-600 mt-0.5">{errors.nip}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">REGON</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              value={form.regon}
              onChange={(e) => setForm({ ...form, regon: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">KRS</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              value={form.krs}
              onChange={(e) => setForm({ ...form, krs: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.is_vat_payer}
              onChange={(e) => setForm({ ...form, is_vat_payer: e.target.checked })}
              className="rounded"
            />
            Vatowiec (czynny podatnik VAT)
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded"
            />
            Aktywny
          </label>
        </div>

        {form.is_vat_payer && (
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Numer VAT UE</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.eu_vat_number}
              onChange={(e) => setForm({ ...form, eu_vat_number: e.target.value })}
              placeholder="opcjonalnie"
            />
          </div>
        )}
      </div>

      {/* Addresses */}
      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Adresy</h2>
        <AddressFields
          label="Adres rejestrowy"
          value={form.registered_address}
          onChange={(a) => setForm({ ...form, registered_address: a })}
        />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={useCorr}
            onChange={(e) => {
              setUseCorr(e.target.checked);
              if (e.target.checked && !form.correspondence_address) {
                setForm({ ...form, correspondence_address: emptyAddress() });
              }
            }}
            className="rounded"
          />
          Inny adres korespondencyjny
        </label>
        {useCorr && form.correspondence_address && (
          <AddressFields
            label="Adres korespondencyjny"
            value={form.correspondence_address}
            onChange={(a) => setForm({ ...form, correspondence_address: a })}
          />
        )}
      </div>

      {/* Banking */}
      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Dane bankowe</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Nr rachunku bankowego (IBAN)</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
              value={form.bank_account_number}
              onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
              placeholder="PL61109010140000071219812874"
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Nazwa banku</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Kontakt</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Osoba kontaktowa</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">E-mail</label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Telefon</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Strona WWW</label>
            <input
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white dark:bg-gray-800 p-6 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Uwagi wewnętrzne</h2>
        <textarea
          rows={3}
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Opcjonalne notatki"
        />
      </div>

      {errors.detail && <p className="text-sm text-red-600 mb-4">{errors.detail}</p>}

      <div className="flex gap-3">
        <Button loading={isPending} onClick={handleSubmit}>
          {isEdit ? "Zapisz zmiany" : "Utwórz klienta"}
        </Button>
        <Button variant="secondary" onClick={() => navigate("/clients")}>
          Anuluj
        </Button>
      </div>
    </div>
  );
}
